/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './RoleScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import RolesService from 'api/roles/RolesService';
import { RoleDto } from 'api/roles/models/RoleDto';
import UsersService from 'api/users/UsersService';

type Props = {
};

const RoleScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const roleProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRolesWritePolicy = UsersService.hasPolicies(roleProfile?.policies || [], ['ROLES_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<RoleDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<RoleDto>();
    const [formData, setFormData] = useState({
        id: '',
        name: ''
    });

    const getData = async () => {
        try {
            let result: any = { id: '', name: '' };
            if (id) {
                Loading.show();
                result = await RolesService.getById(id);
                Loading.hide();
            }
            setItem(result);
            setFormData(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get role information', error);
            toast.error(t('messages.error_load_info'));
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/security/roles/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/security/roles');
        }
    }
    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await RolesService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete company', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: RoleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: RoleDto = {
                id: formData.id,
                name: formData.name,
                checked: true
            };

            if (model && model.id) {
                await RolesService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await RolesService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast(t('messages.role_already_exists'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the role with id: ${form.id}`, error);
                toast.error(t('messages.record_save_error'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('security.roles.title')}>
            <Form onSubmit={onSubmit} noValidate validated={validated}>
                <Row>
                    <Col xs={12} >
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>{t('security.roles.name')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="name" type="text" defaultValue={item?.name} readOnly={isDetails} onChange={handleChange} placeholder={t('security.roles.name')} />
                        </Form.Group>
                    </Col>
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasRolesWritePolicy && !item?.readOnly &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as RoleDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasRolesWritePolicy && !item?.readOnly &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => { navigateTo('edit', item?.id); }}>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasRolesWritePolicy && !item?.readOnly &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default RoleScreen;
